import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import AiResponseRenderer from '../../components/AiResponseRenderer';
import AdaptivePageShell from '../../features/adaptive-learning/AdaptivePageShell';

const EXPERIENCE = ['Junior', 'Mid', 'Senior', 'Staff'];
const DURATIONS = [15, 30, 45];
const DIFFICULTIES = ['Medium', 'Hard'];

function readExpectedAnswer(data) {
  if (!data || typeof data !== 'object') return '';
  const keys = [
    'expectedAnswer',
    'idealAnswer',
    'correctAnswer',
    'referenceAnswer',
    'modelAnswer',
  ];
  for (const key of keys) {
    const val = data[key];
    if (val != null && String(val).trim()) return String(val).trim();
  }
  const text = data.evaluation;
  if (typeof text !== 'string') return '';
  const match = text.match(
    /(?:^|\n)(?:#+\s*|\*\*)?(?:expected|ideal|model|reference|correct)\s*answer[^*\n]*(?:\*\*)?\s*:?\s*\n([\s\S]*?)(?=\n#+\s|\n\*\*[^*]+\*\*|$)/i,
  );
  return match?.[1]?.trim() || '';
}

function InterviewReport({ report }) {
  const dims = report.dimensions || report.Dimensions || {};
  const strong = report.strongTopics || report.strong || [];
  const weak = report.needsImprovement || report.weak || [];
  const steps = report.recommendedNextSteps || [];

  return (
    <div className="adaptive-card">
      <h2>Interview summary</h2>
      <p className="adaptive-disclaimer">AI assessment — for practice only.</p>
      {Object.keys(dims).length > 0 && (
        <div className="interview-dimensions">
          {Object.entries(dims).map(([key, val]) => (
            <div key={key} className="interview-dim-row">
              <span>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
              <strong>{String(val)}</strong>
            </div>
          ))}
        </div>
      )}
      {Array.isArray(strong) && strong.length > 0 && (
        <p><strong>Strong:</strong> {strong.join(', ')}</p>
      )}
      {Array.isArray(weak) && weak.length > 0 && (
        <p><strong>Needs improvement:</strong> {weak.join(', ')}</p>
      )}
      {Array.isArray(steps) && steps.length > 0 && (
        <ul>
          {steps.map((s) => (
            <li key={String(s)}>{String(s)}</li>
          ))}
        </ul>
      )}
      <Link to="/skill-gap" className="learn-btn learn-btn--primary">
        Plan skill gaps
      </Link>
      <Link to="/hub/today" className="learn-btn learn-btn-secondary" style={{ marginLeft: 8 }}>
        Today&apos;s plan
      </Link>
    </div>
  );
}

export default function InterviewPage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const [role, setRole] = useState('Backend Engineer');
  const [experience, setExperience] = useState('Senior');
  const [skills, setSkills] = useState('Java, Spring, System Design');
  const [duration, setDuration] = useState(30);
  const [difficulty, setDifficulty] = useState('Hard');
  const [interviewId, setInterviewId] = useState(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [evaluation, setEvaluation] = useState('');
  const [expectedAnswer, setExpectedAnswer] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const start = async () => {
    setBusy(true);
    setError('');
    setReport(null);
    setEvaluation('');
    setExpectedAnswer('');
    try {
      const res = await apiClient.post('/api/v1/interviews', {
        role,
        experienceLevel: experience,
        skills: skills.split(',').map((s) => s.trim()).filter(Boolean),
        durationMinutes: duration,
        difficulty,
      });
      setInterviewId(res.data.interviewId);
      setQuestion(res.data.question);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not start interview. Check AI quota.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!interviewId || !answer.trim()) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.post(`/api/v1/interviews/${interviewId}/answer`, { answer: answer.trim() });
      setEvaluation(res.data.evaluation);
      setExpectedAnswer(readExpectedAnswer(res.data));
      if (res.data.completed) {
        setReport(res.data.report);
        setQuestion('');
        setAnswer('');
      } else {
        setQuestion(res.data.nextQuestion);
        setAnswer('');
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Submit failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageMeta title="Mock interview" description="AI interview practice with session report." />
      <AdaptivePageShell userProfile={userProfile}>
        <header className="adaptive-hero">
          <h1>Mock interview</h1>
          <p className="adaptive-disclaimer">AI assessment for practice — not an objective hiring score.</p>
        </header>

        {error && <p className="adaptive-error" role="alert">{error}</p>}

        {!interviewId && (
          <div className="adaptive-card">
            <label className="adaptive-label">
              Role
              <input className="learn-input" value={role} onChange={(e) => setRole(e.target.value)} />
            </label>
            <label className="adaptive-label">
              Experience
              <select className="learn-input" value={experience} onChange={(e) => setExperience(e.target.value)}>
                {EXPERIENCE.map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="adaptive-label">
              Skills (comma-separated)
              <input className="learn-input" value={skills} onChange={(e) => setSkills(e.target.value)} />
            </label>
            <div className="adaptive-time-presets">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`adaptive-time-chip${duration === d ? ' adaptive-time-chip--active' : ''}`}
                  onClick={() => setDuration(d)}
                >
                  {d} min
                </button>
              ))}
            </div>
            <div className="adaptive-time-presets">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`adaptive-time-chip${difficulty === d ? ' adaptive-time-chip--active' : ''}`}
                  onClick={() => setDifficulty(d)}
                >
                  {d}
                </button>
              ))}
            </div>
            <button type="button" className="learn-btn learn-btn--primary" disabled={busy} onClick={start}>
              {busy ? 'Starting…' : 'Start interview'}
            </button>
          </div>
        )}

        {question && (
          <section className="adaptive-card">
            <h2>Question</h2>
            <AiResponseRenderer content={question} />
            <label className="adaptive-label">
              Your answer
              <textarea
                className="learn-input import-textarea"
                rows={6}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer as you would in an interview…"
                disabled={busy}
              />
            </label>
            <button
              type="button"
              className="learn-btn learn-btn--primary"
              disabled={busy || !answer.trim()}
              onClick={submit}
            >
              {busy ? 'Evaluating…' : 'Submit answer'}
            </button>
          </section>
        )}

        {evaluation && !report && (
          <section className="adaptive-card">
            <h2>AI assessment</h2>
            <AiResponseRenderer content={evaluation} />
            <div className="interview-expected-answer">
              <h3>Expected answer</h3>
              {expectedAnswer ? (
                <AiResponseRenderer content={expectedAnswer} />
              ) : (
                <p className="adaptive-disclaimer">
                  Reference answer was not returned by the server. Redeploy the latest backend (interview API) and try
                  again.
                </p>
              )}
            </div>
          </section>
        )}

        {report && <InterviewReport report={report} />}
      </AdaptivePageShell>
    </>
  );
}
